import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PickingInfo } from '@deck.gl/core/typed';
import { DeckGLRef } from '@deck.gl/react/typed';
import { ViewStateChangeParameters } from '@deck.gl/core/typed/controllers/controller';
import { addClusterSelection, clearLassoSelection } from 'store/carto/reducer';
import { getSelectedCluster, getSelectedLasso } from 'store/carto/selectors';
import { Asset } from 'components/common/types/Asset.types';
import { ViewState } from '@carto/react-redux';
import { CardPosition } from './PlannerMap.types';

type UseSelectionCardStickyPosition = ({ setViewState }: UseSelectionCardStickyPositionProps) => {
  onClusterClick: (info: PickingInfo) => void;
  onViewportChange: (e: ViewStateChangeParameters) => void;
  cardPosition: CardPosition;
  deckRef: RefObject<DeckGLRef> | null;
};

type UseSelectionCardStickyPositionProps = {
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
};

export const useSelectionCardStickyPosition: UseSelectionCardStickyPosition = ({ viewState, setViewState }) => {
  const dispatch = useDispatch();
  const selectedCluster = useSelector(getSelectedCluster);
  const [initialCardPositionLatLong, setInitialCardPositionLatLong] = useState([0, 0]);
  const [cardPosition, setCardPosition] = useState<CardPosition>({
    position: [0, 0],
    object: 'cluster',
  });
  const selectedLasso = useSelector(getSelectedLasso);

  const deckRef = useRef<DeckGLRef>(null);

  const onNewClusterPositionSelection = useCallback(
    (long: number, lat: number, object: CardPosition['object'], coordinate?: number[]): void => {
      const currentViewport = deckRef.current?.deck?.getViewports?.()[0];
      if (!currentViewport || !Number.isFinite(long) || !Number.isFinite(lat)) return;

      deckRef?.current?.deck?.setProps({
        viewState: {
          longitude: long,
          latitude: lat,
          zoom: currentViewport.zoom,
        },
      });

      if (currentViewport && coordinate) {
        setViewState({
          ...viewState,
          longitude: long || viewState.longitude,
          latitude: lat || viewState.latitude,
        });
        setInitialCardPositionLatLong([long, lat]);
        setCardPosition({ position: currentViewport.project([long, lat]) as [number, number], object });
      }
    },
    [viewState],
  );

  useEffect(() => {
    if (selectedLasso.anchor) {
      onNewClusterPositionSelection(selectedLasso.anchor[0], selectedLasso.anchor[1], 'lasso', selectedLasso.anchor);
    }
  }, [selectedLasso.polygonId]);

  const onClusterClick = (info: PickingInfo): void => {
    if (!info.object || info.object.geometry || info.object.assets === undefined) return;

    if (info.object.assets.length) {
      const [{ longitude: long, latitude: lat, frameId, furnitureCode }] = info.object.assets;

      if (frameId === selectedCluster.furnitureCode) return;

      const isEveryAssetSameFurniture = info.object.assets.every((asset: Asset) => {
        const isFurnitureCodeSame = furnitureCode && asset.furnitureCode === furnitureCode;
        const isPositionSame = long === asset.longitude && lat === asset.latitude;

        return isFurnitureCodeSame || isPositionSame;
      });

      if (!isEveryAssetSameFurniture) return;

      onNewClusterPositionSelection(long, lat, 'cluster', info.coordinate);

      dispatch(clearLassoSelection());
      const asset = info.object.assets[0];

      dispatch(
        addClusterSelection({
          furnitureCode: asset.furnitureCode ?? `${asset.longitude}, ${asset.latitude}`,
          assets: info.object.assets,
        }),
      );

      return;
    }

    const {
      coordinate,
      object,
      object: { longitude: long, latitude: lat, frameId },
    } = info;

    if (frameId === selectedCluster.furnitureCode) return;

    onNewClusterPositionSelection(long, lat, 'cluster', coordinate);

    dispatch(clearLassoSelection());
    dispatch(
      addClusterSelection({
        furnitureCode: object.furnitureCode ?? `${long}, ${lat}`,
        assets: [object],
      }),
    );
  };

  const onViewportChange = useCallback(
    (e: ViewStateChangeParameters): void => {
      const viewport = deckRef.current?.deck?.getViewports?.()[0];

      if (viewport) {
        const [aX, aY] = viewport.project(initialCardPositionLatLong);
        const [bX, bY] = viewport.project([e.viewState.longitude, e.viewState.latitude]);
        const dx = aX - bX + e.viewState.width / 2;
        const dy = aY - bY + e.viewState.height / 2;

        setCardPosition((prev) => ({
          ...prev,
          position: [dx, dy] as [number, number],
        }));
      }
    },
    [initialCardPositionLatLong],
  );

  return { onClusterClick, onViewportChange, cardPosition, deckRef };
};
